
import { useState, useEffect } from 'react';
import { supabase } from '../../../supabaseClient';

interface Clip {
  id: string;
  title: string;
  description: string | null;
  clip_url: string;
  thumbnail_url: string | null;
  duration: number;
  views: number;
  likes: number;
  legend_used: string | null;
  clip_type: string;
  tags: string[] | null;
  created_at: string;
  user_id: string;
  profiles?: {
    username: string;
    avatar_url: string | null;
  };
}

interface ClipsGalleryProps {
  refreshTrigger?: number;
}

export default function ClipsGallery({ refreshTrigger }: ClipsGalleryProps) {
  const [filter, setFilter] = useState('all');
  const [visibleCount, setVisibleCount] = useState(6);
  const [clips, setClips] = useState<Clip[]>([]);
  const [loading, setLoading] = useState(true);

  const mockClips = [
    {
      id: 'mock-1',
      title: 'Insane 0-to-Death Combo',
      user_id: '',
      description: null,
      clip_url: '',
      duration: 154,
      views: 15420,
      likes: 892,
      thumbnail_url: 'https://public.readdy.ai/ai/img_res/ca32cddb880d8e26edd44875d2726b34.jpg',
      clip_type: 'combo',
      legend_used: 'Mordex',
      tags: ['combo', 'ranked'],
      created_at: new Date().toISOString(),
      profiles: { username: 'ShadowStrike', avatar_url: null }
    },
    {
      id: 'mock-2',
      title: 'Perfect Edge Guard',
      user_id: '',
      description: null,
      clip_url: '',
      duration: 98,
      views: 12350,
      likes: 743,
      thumbnail_url: 'https://readdy.ai/api/search-image?query=Brawlhalla%20platform%20fighting%20game%20edge%20guarding%20moment%2C%20character%20defending%20stage%20edge%20with%20weapon%20attack%2C%20competitive%20fighting%20scene%20with%20red%20energy%20effects%20and%20dramatic%20camera%20angle&width=600&height=400&seq=clip-2&orientation=landscape',
      clip_type: 'clutch',
      legend_used: 'Hattori',
      tags: ['edgeguard', 'defense'],
      created_at: new Date().toISOString(),
      profiles: { username: 'PhantomEdge', avatar_url: null }
    },
    {
      id: 'mock-3',
      title: 'Triple Kill Clutch',
      user_id: '',
      description: null,
      clip_url: '',
      duration: 187,
      views: 18920,
      likes: 1205,
      thumbnail_url: 'https://readdy.ai/api/search-image?query=Brawlhalla%20multiple%20opponents%20battle%20scene%2C%20character%20fighting%20three%20enemies%20simultaneously%20with%20special%20effects%2C%20epic%20fighting%20game%20moment%20with%20red%20and%20gold%20lighting%20effects&width=600&height=400&seq=clip-3&orientation=landscape',
      clip_type: 'highlight',
      legend_used: 'Artemis',
      tags: ['clutch', 'ffa'],
      created_at: new Date().toISOString(),
      profiles: { username: 'RevenantKing', avatar_url: null }
    },
    {
      id: 'mock-4',
      title: 'Weapon Throw KO',
      user_id: '',
      description: null,
      clip_url: '',
      duration: 45,
      views: 9840,
      likes: 567,
      thumbnail_url: 'https://readdy.ai/api/search-image?query=Brawlhalla%20weapon%20throw%20finishing%20move%2C%20character%20throwing%20weapon%20for%20knockout%20blow%2C%20fighting%20game%20highlight%20with%20motion%20trails%20and%20impact%20effects%2C%20red%20and%20white%20energy%20burst&width=600&height=400&seq=clip-4&orientation=landscape',
      clip_type: 'highlight',
      legend_used: 'Koji',
      tags: ['throw', 'ko'],
      created_at: new Date().toISOString(),
      profiles: { username: 'BladeRunner', avatar_url: null }
    },
    {
      id: 'mock-5',
      title: 'Dodge Read Perfection',
      user_id: '',
      description: null,
      clip_url: '',
      duration: 76,
      views: 11230,
      likes: 689,
      thumbnail_url: 'https://readdy.ai/api/search-image?query=Brawlhalla%20dodge%20and%20counter%20attack%20moment%2C%20character%20predicting%20opponent%20movement%20with%20perfect%20timing%2C%20competitive%20fighting%20game%20tactical%20play%20with%20red%20visual%20effects&width=600&height=400&seq=clip-5&orientation=landscape',
      clip_type: 'combo',
      legend_used: 'Wu Shang',
      tags: ['read', 'punish'],
      created_at: new Date().toISOString(),
      profiles: { username: 'MindReader', avatar_url: null }
    },
    {
      id: 'mock-6',
      title: 'Signature Move Montage',
      user_id: '',
      description: null,
      clip_url: '',
      duration: 234,
      views: 14560,
      likes: 921,
      thumbnail_url: 'https://readdy.ai/api/search-image?query=Brawlhalla%20signature%20special%20move%20compilation%2C%20character%20performing%20ultimate%20ability%20with%20spectacular%20visual%20effects%2C%20fighting%20game%20highlight%20reel%20with%20red%20and%20orange%20energy%20explosions&width=600&height=400&seq=clip-6&orientation=landscape',
      clip_type: 'montage',
      legend_used: 'Scarlet',
      tags: ['montage', 'sigs'],
      created_at: new Date().toISOString(),
      profiles: { username: 'StyleMaster', avatar_url: null }
    }
  ];

  useEffect(() => {
    fetchClips();
  }, [refreshTrigger]);

  const fetchClips = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('clips')
        .select(`
          *,
          profiles:user_id (
            username,
            avatar_url
          )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;

      if (data && data.length > 0) {
        setClips(data);
      } else {
        setClips(mockClips);
      }
    } catch (err) {
      console.error('Error fetching clips:', err);
      setClips(mockClips);
    } finally {
      setLoading(false);
    }
  };

  const categories = [
    { id: 'all', label: 'All Clips', icon: 'ri-play-circle-line' },
    { id: 'highlight', label: 'Highlights', icon: 'ri-star-line' },
    { id: 'combo', label: 'Combos', icon: 'ri-sword-line' },
    { id: 'clutch', label: 'Clutch', icon: 'ri-fire-line' },
    { id: 'funny', label: 'Funny', icon: 'ri-emotion-laugh-line' },
    { id: 'montage', label: 'Montage', icon: 'ri-film-line' }
  ];

  const filteredClips = filter === 'all' 
    ? clips 
    : clips.filter(clip => clip.clip_type === filter);

  const visibleClips = filteredClips.slice(0, visibleCount);
  const hasMore = visibleCount < filteredClips.length;

  const handleLoadMore = () => {
    setVisibleCount(prev => prev + 6);
  };

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleLike = async (clipId: string, currentLikes: number) => {
    try {
      const { error } = await supabase
        .from('clips')
        .update({ likes: currentLikes + 1 })
        .eq('id', clipId);

      if (!error) {
        setClips(prev => prev.map(clip => 
          clip.id === clipId ? { ...clip, likes: clip.likes + 1 } : clip
        ));
      }
    } catch (err) {
      console.error('Error liking clip:', err);
    }
  };

  if (loading) {
    return (
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-center py-20">
            <i className="ri-loader-4-line animate-spin text-red-500 text-4xl mr-3"></i>
            <span className="text-white text-xl">Loading clips...</span>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 bg-gray-900">
      <div className="max-w-7xl mx-auto px-6">
        <div className="mb-12">
          <h2 className="text-4xl font-bold text-white mb-6">Featured Clips</h2>
          
          <div className="flex flex-wrap gap-3">
            {categories.map(cat => (
              <button
                key={cat.id}
                onClick={() => {
                  setFilter(cat.id);
                  setVisibleCount(6);
                }}
                className={`px-6 py-2 rounded-lg font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                  filter === cat.id
                    ? 'bg-red-600 text-white'
                    : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                }`}
              >
                <i className={`${cat.icon} mr-2`}></i>
                {cat.label}
              </button>
            ))}
          </div>
        </div>

        {visibleClips.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-20 h-20 bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-video-line text-gray-600 text-4xl"></i>
            </div>
            <h3 className="text-2xl font-bold text-white mb-3">No Clips Yet</h3>
            <p className="text-gray-400">Be the first to upload a clip in this category!</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {visibleClips.map(clip => (
              <div key={clip.id} className="bg-gray-800 rounded-xl overflow-hidden hover:transform hover:scale-105 transition-all cursor-pointer group">
                <div className="relative">
                  <img 
                    src={clip.thumbnail_url || `https://readdy.ai/api/search-image?query=Brawlhalla%20fighting%20game%20epic%20gameplay%20moment%20with%20dynamic%20combat%20action%2C%20competitive%20esports%20highlight%20with%20red%20and%20orange%20visual%20effects&width=600&height=400&seq=${clip.id}&orientation=landscape`} 
                    alt={clip.title}
                    className="w-full h-64 object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center">
                      <i className="ri-play-fill text-white text-3xl"></i>
                    </div>
                  </div>
                  <div className="absolute top-3 right-3 bg-black/70 px-3 py-1 rounded-lg">
                    <i className="ri-play-circle-fill text-white mr-1"></i>
                    <span className="text-white text-sm font-semibold">{formatDuration(clip.duration || 0)}</span>
                  </div>
                  {clip.legend_used && (
                    <div className="absolute top-3 left-3 bg-red-600 px-3 py-1 rounded-lg">
                      <span className="text-white text-sm font-semibold">{clip.legend_used}</span>
                    </div>
                  )}
                </div>
                
                <div className="p-5">
                  <h3 className="text-xl font-bold text-white mb-2 line-clamp-1">{clip.title}</h3>
                  <div className="flex items-center text-gray-400 mb-4">
                    <i className="ri-user-line mr-2"></i>
                    <span>{clip.profiles?.username || 'Anonymous'}</span>
                  </div>
                  
                  {clip.tags && clip.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-4">
                      {clip.tags.slice(0, 3).map((tag, idx) => (
                        <span key={idx} className="bg-gray-700 text-gray-300 px-2 py-1 rounded text-xs">
                          #{tag}
                        </span>
                      ))}
                    </div>
                  )}
                  
                  <div className="flex items-center justify-between text-gray-400">
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center">
                        <i className="ri-eye-line mr-1"></i>
                        <span className="text-sm">{clip.views.toLocaleString()}</span>
                      </div>
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleLike(clip.id, clip.likes);
                        }}
                        className="flex items-center hover:text-red-400 transition-colors cursor-pointer"
                      >
                        <i className="ri-heart-line mr-1"></i>
                        <span className="text-sm">{clip.likes.toLocaleString()}</span>
                      </button>
                    </div>
                    <button className="text-red-400 hover:text-red-300 transition-colors cursor-pointer">
                      <i className="ri-share-line text-lg"></i>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {hasMore && (
          <div className="text-center mt-12">
            <button 
              onClick={handleLoadMore}
              className="bg-gray-800 hover:bg-gray-700 text-white px-8 py-3 rounded-lg font-semibold transition-colors cursor-pointer whitespace-nowrap"
            >
              Load More Clips
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
